import { useEffect, useMemo } from "react";

import {
  CurrentPosition,
  DeviceData,
  DeviceHealthData,
  ModelAlertSettings,
  Service,
} from "../../../API";
import { getDeviceMakeByModel } from "../../../common/data/deviceMakes";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { skuCatalog } from "../../../common/models/enums";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useGetDevicesByLocationOrTag } from "../../data-hub/components/liveview/hooks/useGetDevicesByLocationOrTag";
import { useListenToDeviceDataChange } from "../../deployment-health/hooks/useListenToDeviceDataChange";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import { ALLOWED_MODELS_BY_TYPE } from "../../system-settings/tabs/skus/variables/skuData";
import { isLegacySKU } from "../../../common/variables/skuOptions";
import { DisplayNameInput, servicesByNodeVariable } from "../variables/devices";
import { useGetServicesByLocation } from "./useGetServicesByLocation";

export interface IModel {
  serviceName: ServiceTypeEnum;
  serviceId: string;
  nodeId: string;
  isRunning?: boolean;
  configuration?: string;
  modelAlertSettingsId?: string;
  modelAlertSettings?: ModelAlertSettings;
}

export type DeviceDataType = Omit<DeviceData, "__typename">;

export interface IDeviceTableRow {
  rowId: string;
  name: string;
  location: DisplayNameInput;
  node: DisplayNameInput;
  makeModelId: string;
  models: IModel[];
  modelsToRun: string[];
  deviceData: DeviceDataType;
  currentPosition?: CurrentPosition | null;
  healthData: Omit<DeviceHealthData, "__typename">;
  isOnline: boolean;
  cameraIpAddress: string;
  rtspTemplate: string;
  skuNumber?: string | null;
  serialNumber?: string | null;
  componentSerialNumber?: string | null;
  parentProductSerialNumber?: string | null;
}

interface IUseDeviceTableRows {
  rows: IDeviceTableRow[];
  loading: boolean;
}

// this hook gets devices by location or tag locations &&& their respective services &&& their respective deviceMakes (to render compatiable models)

// get all devices -> location or customer -> tags
// get all services -> location (does not work for tags!) -> tags
// get all deviceMakes (ok for scale)
export const useDeviceTableRows = (): IUseDeviceTableRows => {
  const selectedLocation = useSelectedLocationDropdown();

  const {
    devices,
    loading: deviceLoading,
    refetch,
  } = useGetDevicesByLocationOrTag();

  const { getCachedLocation } = useLocationFromCache();

  const { data: deviceHealthData } = useListenToDeviceDataChange();

  // Fetch services for the selected location (single query instead of N queries per device)
  const { servicesByDeviceId, loading: servicesLoading } =
    useGetServicesByLocation(selectedLocation?.value);

  const isLoading = deviceLoading || servicesLoading;

  // Update the servicesByLocationVariable with all services for backward compatibility

  const rowsByLocation: IDeviceTableRow[] = useMemo(() => {
    const mappedData = !isLoading
      ? devices?.map(item => {
          const cachedLocation = getCachedLocation(item?.locationId ?? "");

          // Get services for this device from the pre-fetched map
          const deviceServices =
            servicesByDeviceId.get(item?.id as string) ?? [];

          // Convert services to IModel format
          const models: IModel[] = deviceServices.map(
            (service: Service): IModel => ({
              serviceName: service.serviceType as ServiceTypeEnum,
              serviceId: service.id as string,
              nodeId: service.nodeId as string,
              isRunning: service.isRunning ?? false,
              configuration: service.configuration ?? undefined,
              modelAlertSettingsId: service.modelAlertSettingsId ?? undefined,
              modelAlertSettings: service.modelAlertSettings ?? undefined,
            })
          );

          // Determine modelsToRun: SKU-based devices use skuCatalog, legacy devices use DeviceMakes
          const getModelsToRun = (): string[] => {
            const skuNumber = item?.skuNumber;
            const makeModelId = item?.makeModelId;

            // SKU-onboarded device with valid SKU and subComponents
            if (skuNumber && skuCatalog[skuNumber]?.subComponents?.length > 0) {
              const component = skuCatalog[skuNumber].subComponents.find(
                sub => sub.makeModelId === makeModelId
              );

              const legacyDeviceMake = getDeviceMakeByModel(makeModelId);

              if (legacyDeviceMake) {
                // old legacy devices have a makeModel that we lookup in deviceMakes.json for modelsToRun
                return legacyDeviceMake.modelsToRun ?? [];
              } else if (component) {
                // new SKU system device has its own lookup in SKUS.json for modelsToRUn
                return ALLOWED_MODELS_BY_TYPE[component.type] ?? [];
              } else {
                return ALLOWED_MODELS_BY_TYPE["LEGACY"] ?? [];
              }
            }

            // Devices with no SKU or Legacy SKU ("000-000") should still show models.
            // Check deviceMakes.json first for device-specific allowed models; fall back to all
            // Legacy models if the makeModelId isn't found there.
            if (!skuNumber || isLegacySKU(skuNumber)) {
              const legacyDeviceMake = getDeviceMakeByModel(makeModelId);

              if (legacyDeviceMake) {
                return legacyDeviceMake.modelsToRun ?? [];
              }

              return ALLOWED_MODELS_BY_TYPE["LEGACY"] ?? [];
            }

            return [];
          };

          const rawNodeId = item?.nodeId ?? "";
          const locationId = item?.locationId ?? "";
          const resolvedNodeId =
            rawNodeId &&
            !rawNodeId.includes("#L#") &&
            rawNodeId.includes("#N#") &&
            locationId
              ? `${locationId}#N#${rawNodeId.split("#N#")[1]}`
              : rawNodeId;

          return {
            rowId: item?.id as string,
            name: item?.name as string,
            location: {
              id: item?.locationId as string,
              name: cachedLocation?.name as string,
            },
            node: {
              id: resolvedNodeId,
              name: item?.nodeName as string,
            },
            modelsToRun: getModelsToRun(),
            models,
            makeModelId: item?.makeModelId as string,
            deviceData: item?.deviceData,
            currentPosition: item?.currentPosition,
            healthData: item?.healthData ?? "{}",
            isOnline: Boolean(item?.healthData?.isOnline),
            cameraIpAddress: item?.cameraIpAddress as string,
            rtspTemplate: "", // TODO: Add rtspTemplate to Device type if needed
            skuNumber: item?.skuNumber,
            serialNumber: item?.serialNumber,
            componentSerialNumber: item?.componentSerialNumber,
            parentProductSerialNumber: item?.parentProductSerialNumber,
          };
        }) ?? []
      : [];

    let rows = [...mappedData];

    if (selectedLocation?.value) {
      rows = rows?.filter(item => {
        const nameOfLocation = item.location.id;

        return nameOfLocation === selectedLocation?.value;
      });
    }

    return rows;
  }, [
    devices,
    window.location.hash,
    isLoading,
    selectedLocation?.value,
    servicesByDeviceId,
  ]);

  // Note: Tag-based service fetching is now handled by useGetServicesByDevices
  // which fetches services for all devices regardless of location or tag filtering

  useEffect(() => {
    return () => {
      // Clear the servicesByLocation variable when the component is unmounted
      servicesByNodeVariable([]);
    };
  }, []);

  const rows = rowsByLocation;

  const memoizedDevicesRows =
    useMemo(() => {
      const isDeviceDataFromSubscription = deviceHealthData?.listenToDeviceData;

      if (!isDeviceDataFromSubscription) return rows;

      const updatedRows = rows.map(device => {
        if (isDeviceDataFromSubscription.id === device?.rowId) {
          // Check if the online status has changed
          if (
            isDeviceDataFromSubscription?.healthData?.isOnline !==
            device?.healthData?.isOnline
          ) {
            refetch();
          }

          return {
            ...device,
            isOnline: Boolean(
              isDeviceDataFromSubscription?.healthData?.isOnline
            ),
          };
        }

        return {
          ...device,
        };
      });

      return updatedRows;
    }, [deviceHealthData, rows]) ?? [];

  const sortedRows = [...memoizedDevicesRows]
    ?.sort((a, b) => {
      if (!a.skuNumber && !b.skuNumber) {
        return 0;
      }

      if (!a.skuNumber) {
        return 1;
      }

      if (!b.skuNumber) {
        return -1;
      }

      return a.skuNumber?.localeCompare(b.skuNumber);
    })
    .map((item, i) => ({ ...item, sequenceNumber: i + 1 }));

  return {
    rows: sortedRows,
    loading: isLoading,
  };
};
